import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../auth/apiClient';

export default function useBellNotifications(identityKey, sortMode) {
  const [result, setResult] = useState({ identityKey, items: [] });
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  const request = useRef(null);
  const dismissed = useRef(new Set());
  const refresh = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const version = ++generation.current;
    if (!identityKey) { setResult({ identityKey, items: [] }); setLoading(false); return []; }
    setLoading(true);
    try {
      const response = await apiFetch(`/api/me/notifications?sort=${encodeURIComponent(sortMode)}`, { signal: controller.signal });
      if (!response.ok) throw new Error('Notification fetch failed');
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Invalid notification response');
      if (controller.signal.aborted || version !== generation.current) return null;
      const items = data.filter(item => !dismissed.current.has(String(item.id)));
      setResult({ identityKey, items });
      return items;
    } catch (_) {
      if (!controller.signal.aborted && version === generation.current) setResult({ identityKey, items: [] });
      return null;
    } finally {
      if (version === generation.current) setLoading(false);
    }
  }, [identityKey, sortMode]);
  useEffect(() => {
    dismissed.current = new Set();
    setResult({ identityKey, items: [] });
    const visibleRefresh = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const sessionChanged = () => {
      generation.current += 1;
      request.current?.abort();
      dismissed.current.clear();
      setResult({ identityKey, items: [] });
      refresh();
    };
    refresh();
    const timer = window.setInterval(visibleRefresh, 30000);
    window.addEventListener('focus', visibleRefresh);
    document.addEventListener('visibilitychange', visibleRefresh);
    window.addEventListener('auth:session-changed', sessionChanged);
    window.addEventListener('storage', sessionChanged);
    return () => {
      generation.current += 1;
      request.current?.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', visibleRefresh);
      document.removeEventListener('visibilitychange', visibleRefresh);
      window.removeEventListener('auth:session-changed', sessionChanged);
      window.removeEventListener('storage', sessionChanged);
    };
  }, [identityKey, refresh]);
  const remove = useCallback(id => {
    dismissed.current.add(String(id));
    setResult(previous => ({ ...previous, items: previous.items.filter(item => String(item.id) !== String(id)) }));
  }, []);
  return { notifications: result.identityKey === identityKey ? result.items : [], loading, refresh, remove };
}
